import React from "react";
import FormField from "./FormField";
import RepeatableSectionRenderer from "./RepeatableSectionRenderer";
import { getVisibleFields } from "utilities/schemaEval";
import { fieldPercentStatus } from "utilities/percentBounds";

/**
 * Generic schema-driven step renderer.
 * Renders fields from a step schema definition using the existing FormField component.
 * Supports grouped sections (with optional titled containers), mixed grid layouts,
 * repeatable array sections, and conditional visibility.
 */
// `storedData` (optional) is the record as the server last had it. SOUND-777:
// an out-of-range percentage that is unchanged from it warns, a changed one is
// an error. Omitted, every out-of-range value is an error.
const StepRenderer = ({ stepSchema, data, storedData, onChange }) => {
  // Dispatch repeatable sections to RepeatableSectionRenderer
  if (stepSchema.sectionType === "repeatable") {
    return (
      <RepeatableSectionRenderer
        stepSchema={stepSchema}
        data={data}
        onChange={onChange}
        fullData={data}
      />
    );
  }

  const sectionData = data?.[stepSchema.key] || {};
  const storedSection = storedData ? storedData[stepSchema.key] || {} : null;
  const percentStatusOf = (field) =>
    fieldPercentStatus(field, sectionData[field.name], {
      storedValue: storedSection ? storedSection[field.name] : undefined,
      hasStored: Boolean(storedSection),
    });

  const handleChange = (field, value) => {
    onChange(stepSchema.key, field, value);
  };

  // Filter fields by conditional visibility
  const visibleFields = getVisibleFields(stepSchema.fields, sectionData, data);

  const renderField = (field) => (
    <FormField
      key={field.name}
      label={field.label}
      name={field.name}
      value={sectionData[field.name] || ""}
      onChange={handleChange}
      type={field.type || "text"}
      required={field.required}
      placeholder={field.placeholder}
      options={field.options}
      rows={field.rows}
      tooltip={field.tooltip}
      otherField={field.otherField}
      otherValue={sectionData[field.otherField] || ""}
      onOtherChange={(otherName, otherVal) => handleChange(otherName, otherVal)}
      // SOUND-777: transaction-type percentages are bounded 0–100. The same
      // rule guards the save (useRegistration / ApplicationEditor) and the
      // Registration tab's inline editor.
      error={percentStatusOf(field).error}
      warning={percentStatusOf(field).warning}
    />
  );

  // If step has groups, render fields in grouped sections
  if (stepSchema.groups) {
    const groupDefs = stepSchema.groups;
    const groupedFields = {};

    // Bucket visible fields by group key
    for (const field of visibleFields) {
      const groupKey = field.group || "_default";
      if (!groupedFields[groupKey]) groupedFields[groupKey] = [];
      groupedFields[groupKey].push(field);
    }

    return (
      <div className="space-y-6">
        <div className="text-center mb-8">
          <h2 className="text-2xl font-normal text-foreground mb-2">
            {stepSchema.title}<span className="text-brand">.</span>
          </h2>
          <p className="text-muted-foreground font-light">
            {stepSchema.description}
          </p>
        </div>

        {groupDefs.map((groupDef) => {
          const fields = groupedFields[groupDef.key] || [];
          if (fields.length === 0) return null;

          const gridClass = getGridClass(fields);
          const content = (
            <div className={gridClass}>
              {fields.map(renderField)}
            </div>
          );

          // Wrap in styled container if group has a title
          if (groupDef.title) {
            const containerClass =
              groupDef.variant === "risk"
                ? "bg-red-900 bg-opacity-20 border border-red-500 border-opacity-30 p-6 rounded-lg"
                : "bg-muted/30 border border-brand/30 p-6 rounded-lg";
            const titleClass =
              groupDef.variant === "risk"
                ? "text-lg font-normal text-red-400 mb-4"
                : "text-lg font-normal text-brand mb-4";

            return (
              <div key={groupDef.key} className={containerClass}>
                <h3 className={titleClass}>{groupDef.title}</h3>
                {content}
              </div>
            );
          }

          return (
            <React.Fragment key={groupDef.key}>{content}</React.Fragment>
          );
        })}
      </div>
    );
  }

  // No groups — render visible fields in sections based on gridCols transitions
  const blocks = [];
  let currentBlock = null;

  for (const field of visibleFields) {
    const cols = field.gridCols || 2;
    if (!currentBlock || currentBlock.cols !== cols) {
      currentBlock = { cols, fields: [] };
      blocks.push(currentBlock);
    }
    currentBlock.fields.push(field);
  }

  return (
    <div className="space-y-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-normal text-foreground mb-2">
          {stepSchema.title}<span className="text-brand">.</span>
        </h2>
        <p className="text-muted-foreground font-light">
          {stepSchema.description}
        </p>
      </div>

      {blocks.map((block, i) => (
        <div
          key={i}
          className={
            block.cols === 1
              ? "grid grid-cols-1 gap-6"
              : "grid grid-cols-1 md:grid-cols-2 gap-6"
          }
        >
          {block.fields.map(renderField)}
        </div>
      ))}
    </div>
  );
};

/**
 * Determine the grid class based on the gridCols values in a set of fields.
 */
function getGridClass(fields) {
  const hasThirds = fields.some((f) => f.gridCols === 3);
  const hasHalves = fields.some((f) => f.gridCols === 2);
  const hasFullWidth = fields.some((f) => f.gridCols === 1);

  if (hasThirds) {
    return "grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4";
  }
  if (hasHalves && hasFullWidth) {
    return "grid grid-cols-1 md:grid-cols-2 gap-4";
  }
  if (hasHalves) {
    return "grid grid-cols-1 md:grid-cols-2 gap-4";
  }
  return "grid grid-cols-1 gap-4";
}

export default StepRenderer;
